import { Dictionary, get, isArray, isObject, keys } from '@direct-frontend/stdlib';

import { BaseField, BaseFields } from '../../fields/types';
import { getActualUnionFieldConstituent } from '../../fields/utils/getActualUnionFieldConstituent';
import { stringifyFieldIdPath } from '../../fields/utils/stringifyFieldIdPath';
import { FormSliceState } from '../types';

import { getFieldPathById } from './getFieldPathByFieldId';

interface GetFieldsContext {
    fields: BaseFields;
    fieldsContextArgs: Dictionary<unknown>;
    fieldsState: FormSliceState['forms'][string]['fieldsState'];
    formValue: unknown;
}

// Вычисляет текущее значение контекста переданных полей на основании значения и состояния формы
export function getFieldsContext({ fields, fieldsContextArgs, fieldsState, formValue }: GetFieldsContext) {
    const fieldsContext: FormSliceState['forms'][string]['context']['fieldsContext'] = {};

    for (const fieldName of keys(fields)) {
        const field = fields[fieldName];
        const fieldContextArg = fieldsContextArgs[fieldName];

        if (!field) continue;

        getFieldAndChildrenContext(
            { field, fieldId: fieldName, fieldContextArg, fieldsState, formValue },
            fieldsContext
        );
    }

    return fieldsContext;
}

interface GetFieldContextOptions {
    field: BaseField;
    fieldId: string;
    fieldContextArg: unknown;
    fieldsState: FormSliceState['forms'][string]['fieldsState'];
    formValue: unknown;
}

function getFieldAndChildrenContext(
    { field, fieldId, fieldContextArg, fieldsState, formValue }: GetFieldContextOptions,
    fieldsContext: FormSliceState['forms'][string]['context']['fieldsContext'] = {}
) {
    const fieldState = fieldsState[fieldId];

    if (!fieldState) {
        return fieldsContext;
    }

    // В этой функции нет связи между полем и значением аргумента контекста – связь гарантируется публичным интерфейсом
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    const contextArg = fieldContextArg as never;

    if (field.type === 'primitive' && field.context) {
        const context = field.context?.(contextArg);
        if (context) {
            fieldsContext[fieldId] = context;
        }
    }

    if (field.type === 'object') {
        const context: Dictionary<unknown> = field.context?.(contextArg) ?? {};
        if (context?.object) {
            fieldsContext[fieldId] = context?.object;
        }

        for (const fieldName of keys(field.fields)) {
            const innerField = field.fields[fieldName];
            if (!innerField) {
                continue;
            }

            getFieldAndChildrenContext(
                {
                    field: innerField,
                    fieldId: stringifyFieldIdPath([fieldId, fieldName]),
                    fieldContextArg: isObject(context.fields) ? context.fields[fieldName] : undefined,
                    fieldsState,
                    formValue,
                },
                fieldsContext
            );
        }
    }

    if (field.type === 'array') {
        const fieldValue = get(
            formValue,
            getFieldPathById(() => fieldsState, fieldId)
        );

        const context: Dictionary<unknown> = field.context?.(contextArg) ?? {};
        if (context?.array) {
            fieldsContext[fieldId] = context?.array;
        }

        if (!isArray(fieldValue) || !('order' in fieldState)) {
            return;
        }

        for (let index = 0; index < fieldValue.length; index = index + 1) {
            const arrayItemFieldId = fieldState.order[index];
            if (!arrayItemFieldId) {
                continue;
            }

            getFieldAndChildrenContext(
                {
                    field: field.field,
                    fieldId: arrayItemFieldId,
                    fieldContextArg: context.field,
                    fieldsState,
                    formValue,
                },
                fieldsContext
            );
        }
    }

    if (field.type === 'union') {
        const fieldValue = get(
            formValue,
            getFieldPathById(() => fieldsState, fieldId)
        );

        if (!isObject(fieldValue)) {
            return;
        }

        getFieldAndChildrenContext(
            {
                field: getActualUnionFieldConstituent(field, fieldValue),
                fieldId,
                fieldContextArg,
                formValue,
                fieldsState,
            },
            fieldsContext
        );
    }

    return fieldsContext;
}
